import { Card, CardContent } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const labels = {
  regressions: 'Regressions', new_issues: 'New issues', resolved_issues: 'Resolved', persistent_issues: 'Persistent',
  added_urls: 'Added URLs', removed_urls: 'Removed URLs', changed_pages: 'Changed pages',
};
const display = value => value == null ? 'Unknown' : typeof value === 'object' ? JSON.stringify(value) : String(value);
const title = key => key?.replace(/_/g, ' ') || 'Evidence';
const safeUrl = url => /^https?:\/\//i.test(url || '') ? url : undefined;

export function EvidenceList({ label, rows = [] }) {
  return <details className="border-t border-white/10 py-3">
    <summary className="text-sm text-zinc-200 cursor-pointer">{label} <span className="text-zinc-500">({rows.length})</span></summary>
    {!rows.length && <p className="text-xs text-zinc-500 mt-2">No findings in this group.</p>}
    <div className="space-y-2 mt-2 max-h-96 overflow-auto">
      {rows.map((row, index) => {
        const item = typeof row === 'string' ? { url: row } : row;
        return <details key={`${item.url || item.query || 'row'}-${item.key || index}`} className="text-xs text-zinc-400 py-1">
          <summary className="cursor-pointer break-all text-zinc-300">{item.url || item.page || item.query || 'Evidence'}{item.key && <span className="ml-2 text-zinc-500">{title(item.key)}</span>}</summary>
          {safeUrl(item.url || item.page) && <a href={safeUrl(item.url || item.page)} target="_blank" rel="noreferrer" className="text-blue-400 inline-block my-2">Open URL</a>}
          {item.changes ? <dl className="space-y-2 mt-2">{Object.entries(item.changes).map(([field, change]) => <div key={field}>
            <dt className="text-zinc-200">{title(field)}</dt>
            {'before' in change ? <><dd className="break-all">Before: {display(change.before)}</dd><dd className="break-all">After: {display(change.after)}</dd></> : <dd className="break-all">Change: {display(change.absolute)} · {change.percent == null ? 'No percentage baseline' : `${Number(change.percent).toFixed(1)}%`}</dd>}
          </div>)}</dl> : <dl className="space-y-1 mt-1">{Object.entries(item).filter(([key]) => key !== 'url' && key !== 'key').map(([key, value]) => <div key={key} className="break-all"><dt className="inline text-zinc-300">{title(key)}: </dt><dd className="inline">{display(value)}</dd></div>)}</dl>}
        </details>;
      })}
    </div>
  </details>;
}

export function ChangeVerification({ data, compact = false }) {
  if (!data?.available) return compact ? null : <p className="text-sm text-zinc-400 py-4">{data?.reason || 'Run a new audit to establish a scan baseline. A second audit with the same crawl scope enables comparison.'}</p>;
  return <Card className="bg-[#121212] border-white/10 mb-4" data-testid="change-verification">
    <CardContent className="p-4">
      <h2 className="text-sm font-semibold text-white mb-2">Changes since previous audit</h2>
      <p className="text-xs text-zinc-500 mb-3">Compared with {data.previous_audit_date ? new Date(data.previous_audit_date).toLocaleString() : 'previous completed audit'}</p>
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
        {[['resolved', 'Resolved'], ['regressions', 'Regressions'], ['new', 'New'], ['persistent', 'Persistent']].map(([key, label]) => <div key={key}><strong className={key === 'regressions' ? 'text-red-400' : 'text-white'}>{data.summary?.[key] ?? 0}</strong> <span className="text-zinc-400">{label}</span></div>)}
      </div>
      {!compact && <div className="mt-3">
        <p className="text-xs text-zinc-500 mb-3">{data.coverage_note}</p>
        {Object.entries(labels).map(([key, label]) => <EvidenceList key={key} label={label} rows={data[key]} />)}
      </div>}
    </CardContent>
  </Card>;
}

export function SearchPerformance({ data }) {
  if (!data?.available) return null;
  const performance = data.performance || {};
  const current = performance.current_28d || {};
  const previous = performance.previous_28d || {};
  const format = (key, value) => value == null ? '—' : key === 'ctr' ? `${(value * 100).toFixed(2)}%` : Number(value).toLocaleString(undefined, { maximumFractionDigits: key === 'position' ? 2 : 0 });
  return <div data-testid="search-performance">
    <p className="text-xs text-zinc-500 mb-3">{current.start_date} – {current.end_date} vs {previous.start_date} – {previous.end_date}</p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[['clicks', 'Clicks'], ['impressions', 'Impressions'], ['ctr', 'CTR'], ['position', 'Average position']].map(([key, label]) => {
        const change = performance.changes?.[key];
        const delta = key === 'ctr' ? `${((change?.absolute || 0) * 100).toFixed(2)} percentage points` : key === 'position' ? `${(change?.absolute || 0).toFixed(2)} positions` : change?.percent == null ? 'No percentage baseline' : `${change.percent.toFixed(1)}%`;
        return <div key={key} className="text-sm"><span className="text-xs text-zinc-400">{label}</span><p className="text-xl text-white tabular-nums">{format(key, current[key])}</p><p className="text-xs text-zinc-500">Previous {format(key, previous[key])} · {delta}</p></div>;
      })}
    </div>
  </div>;
}

export function GoogleSearchEvidence({ data, analytics, compact = false }) {
  if (!data?.available) return null;
  return <Card className="bg-[#121212] border-white/10 mb-4" data-testid="google-search-reality">
    <CardContent className="p-4">
      <h2 className="text-sm font-semibold text-white mb-2">Google Search reality</h2>
      <p className="text-xs text-zinc-500 mb-3 break-all">{data.property} · Synced {data.synced_at ? new Date(data.synced_at).toLocaleString() : 'Unknown'}</p>
      <SearchPerformance data={data} />
      {!compact && <div className="mt-4">
        <p className="text-xs text-zinc-500 mb-3">{data.interpretation}</p>
        {analytics?.daily?.length > 0 && <div className="mb-4">
          <h3 className="text-sm text-zinc-300 mb-2">90-day Search trend</h3>
          <div className="h-48" role="img" aria-label="Daily Google Search clicks and impressions">
            <ResponsiveContainer width="100%" height="100%"><LineChart data={analytics.daily}>
              <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis yAxisId="clicks" tick={{ fontSize: 10 }} width={40} />
              <YAxis yAxisId="impressions" orientation="right" tick={{ fontSize: 10 }} width={45} />
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid #444', color: '#fff' }} />
              <Line yAxisId="clicks" dataKey="clicks" name="Clicks" stroke="#3b82f6" dot={false} />
              <Line yAxisId="impressions" dataKey="impressions" name="Impressions" stroke="#a855f7" dot={false} />
            </LineChart></ResponsiveContainer>
          </div>
        </div>}
        {analytics && <><EvidenceList label="Top pages" rows={analytics.top_pages} /><EvidenceList label="Top queries" rows={analytics.top_queries} /></>}
        {[['indexing_discrepancies', 'Indexing discrepancies'], ['canonical_discrepancies', 'Canonical discrepancies'], ['traffic_at_risk', 'Confirmed traffic at risk'], ['previously_visible_not_observed', 'Previously visible URLs not observed — verification needed'], ['search_opportunities', 'Search opportunities'], ['declining_pages', 'Declining pages'], ['growing_pages', 'Growing pages']].map(([key, label]) => <EvidenceList key={key} label={label} rows={data[key]} />)}
        <p className="text-xs text-zinc-500 mt-3">{data.data_note}</p>
      </div>}
    </CardContent>
  </Card>;
}
